// ---------------------------------------------------------------------------
// routeAnalytics — fire-and-forget logger for "why" chip clicks and route
// selections, so we can correlate which scoring topics people explore vs.
// which routes they actually pick. Anonymous-safe (uses a per-browser session
// id) and never blocks the UI.
// ---------------------------------------------------------------------------

import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "pleasant.analyticsSessionId";

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr-session";
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing && existing.length >= 6) return existing;
    const fresh =
      (typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36));
    window.localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return "no-storage-session";
  }
};

/**
 * THE PRODUCT HAPPENS BETWEEN `route_select` AND THE RATING, AND UNTIL NOW
 * NOTHING WATCHED IT.
 *
 * Measured Aug 10: 4,466 rows since June 10, every one of them `route_select`,
 * every one with NULL coordinates. So the dashboard could say "35 people
 * routing" and mean "35 sessions tapped a card" — not that anyone pressed GO,
 * drove a mile, or arrived. Pleasant's whole promise is that the drive was
 * worth the extra twenty minutes, and we could not tell whether a single
 * person had ever completed one.
 *
 * nav_start / nav_complete / nav_abandoned close that gap. With the
 * coordinates (columns that already existed and were never filled) they also
 * answer the question that should be deciding which country to import next:
 * where do people actually drive?
 */
export type RouteInteractionAction =
  | "why_click"
  | "route_select"
  | "route_view"
  | "nav_start"
  | "nav_complete"
  | "nav_abandoned"
  /**
   * Mid-drive "Take fastest".
   *
   * NOT a verdict on the route (Morgan, Aug 10: "bail to fastest is ok because
   * they may realize they are late"). I had this logged as "the purest bail
   * signal" and that reading is wrong — running late is the ordinary reason,
   * and it says nothing about whether the pleasant way was pleasant. Recorded
   * because it's real, but do not put it in a "route quality" numerator.
   */
  | "nav_switch_fastest"
  /**
   * A HEARTBEAT, BECAUSE THE MOST COMMON BAIL LEAVES NO TRACE.
   *
   * nav_abandoned is written by React cleanup — which does not run when the
   * app is killed, force-quit, or swiped away mid-drive. So the exact case
   * Morgan cares about ("they left the app") was the one case that recorded
   * nothing at all, and every drive that ended that way would have looked
   * like a drive that never ended.
   *
   * A ping every few minutes removes the dependency on a final event: a trip
   * with nav_start and no nav_complete was abandoned, and the last ping says
   * how far they got. Nothing has to survive the app dying.
   */
  | "nav_ping";

/** ios | android | web — nothing recorded this before Aug 10, so phone and
 *  browser usage were indistinguishable in every number we had. */
const getPlatform = (): string => {
  try {
    // Imported lazily-ish: this module is loaded on the web too.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (globalThis as any).Capacitor;
    if (cap?.isNativePlatform?.()) return String(cap.getPlatform?.() ?? "native");
  } catch { /* fall through to web */ }
  return "web";
};

/** First and last point of a route polyline, as the trip's endpoints.
 *  Coordinates are [lng, lat] — getting that backwards would put every trip
 *  in the wrong hemisphere, which is exactly the kind of thing that reads as
 *  data until someone plots it. */
export const endpointsOf = (
  coords: [number, number][] | undefined | null,
): { start: { lat: number; lng: number } | null; end: { lat: number; lng: number } | null } => {
  if (!coords || coords.length === 0) return { start: null, end: null };
  const a = coords[0];
  const b = coords[coords.length - 1];
  return {
    start: { lat: a[1], lng: a[0] },
    end: { lat: b[1], lng: b[0] },
  };
};

export interface RouteInteractionInput {
  action: RouteInteractionAction;
  /** Topic key emitted by the scorer (coastal, parks, busy, anchor, …). */
  topic?: string | null;
  /** Human-readable factor/chip text exactly as shown in the UI. */
  factorText?: string | null;
  travelMode?: string | null;
  routeIndex?: number | null;
  routePleasantScore?: number | null;
  routeDistanceM?: number | null;
  routeDurationS?: number | null;
  isRecommended?: boolean | null;
  start?: { lat: number; lng: number } | null;
  end?: { lat: number; lng: number } | null;
  /** Percent of the route actually driven. Bailing at 4% and stopping at 92%
   *  are opposite facts; without this they are the same row. */
  progressPct?: number | null;
  /** One id per drive, generated at nav_start. session_id lives in
   *  localStorage for months, so without this two drives in an evening merge
   *  into one and "did it finish" becomes a guess. */
  tripId?: string | null;
  /** Was a CarPlay screen attached? Captured at nav_start (Aug 14: "can u
   *  tell who is on carplay" — before this flag, nobody could). Only
   *  nav_start carries it; other events of the drive join via trip_id.
   *  Omitted = not measured (old call sites), which the DB stores as NULL
   *  rather than a fake false. */
  carplay?: boolean | null;
}

/** A fresh id for one drive. Called once, at nav_start. */
export const newTripId = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};

/** Best-effort insert. Never throws — analytics must never break the UI. */
export const logRouteInteraction = async (input: RouteInteractionInput): Promise<void> => {
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    const { data: userData } = await supabase.auth.getUser();
    const row = {
      user_id: userData.user?.id ?? null,
      session_id: getSessionId(),
      action: input.action,
      topic: input.topic ?? null,
      factor_text: input.factorText?.slice(0, 200) ?? null,
      travel_mode: input.travelMode ?? null,
      route_index: input.routeIndex ?? null,
      route_pleasant_score: input.routePleasantScore ?? null,
      route_distance_m: input.routeDistanceM ?? null,
      route_duration_s: input.routeDurationS ?? null,
      is_recommended: input.isRecommended ?? null,
      start_lat: input.start?.lat ?? null,
      start_lng: input.start?.lng ?? null,
      end_lat: input.end?.lat ?? null,
      end_lng: input.end?.lng ?? null,
      platform: getPlatform(),
      progress_pct: input.progressPct ?? null,
      trip_id: input.tripId ?? null,
      carplay: input.carplay ?? null,
    };
    // Cast: the generated Supabase types predate the platform / progress_pct /
    // trip_id columns (added Aug 10-11; verified live with an insert probe).
    // The durable fix is regenerating src/integrations/supabase/types.ts —
    // heavier than a stabilization build should carry.
    await supabase.from("route_interactions").insert(row as never);
  } catch {
    /* swallow — analytics is best-effort */
  }
};
